<template>
  <loading-state v-if="loading" variant="more-info" />
  <distribution-card
    v-else
    :title="variant === 'dre' ? 'Distribuição de consumidores' : 'Consumidores'"
    open-new-tab-label="Explorar mais em Classificação RFV"
    open-new-tab-route="/clientes/classificacao-rfv"
    :show-redirect-button="variant === 'dre'"
    :is-pdf="isPdf"
    custom-redirect
    data-userflow-id="consumers-distribution-card"
    :disabled="isEmpty"
    @redirect="handleRedirectToRfv"
  >
    <template v-slot:distribution-content>
      <div class="consumers-distribution-container">
        <div v-if="isDREVariant && !isPdf" class="header-description">
          Entenda como estava a distribuição dos consumidores presentes na análise antes do período analisado.
        </div>
        <card-empty-state v-if="isEmpty" height="100%" class="mt-4" />
        <div v-else class="chart-container">
          <div class="mx-auto">
            <apex-chart id="donut-chart" :width="isDREVariant ? 220 : 180" type="donut" :options="options" :series="series" />
            <p class="under-chart-label mb-1 mt-4">
              Total: <b>{{ quantityTotal | localeString }}</b>
            </p>
            <p v-if="variant === 'home'" class="under-chart-label mt-0 d-flex align-center">
              Novos:<b class="highlight"> +{{ homeClientesCadastrados | localeString | hifen }}</b>
              <mf-quick-actions v-if="!isPdf" v-bind="newClientsQuickAction" icon-color="#0A8533" />
            </p>
          </div>
          <div class="chart-legend-container">
            <div class="chart-legend-header">
              <div>Grupo</div>
              <div>Quantidade</div>
            </div>
            <div class="chart-legend-labels">
              <mf-tooltip
                v-for="metric in metrics"
                :key="metric.id"
                :disabled="isDREVariant"
                max-width="300"
                top
                variant="tertiary"
                :color="metric.tooltipColor"
              >
                <template #activator="{ on }">
                  <div class="legend-label">
                    <div class="chart-group">
                      <div class="group-color-indicator" :style="{ backgroundColor: metric.color }"></div>
                      <span> {{ metric.group }}</span>
                    </div>
                    <div class="d-flex align-center">
                      <span>{{ metric.clients | localeString }}</span>
                      <mf-quick-actions
                        v-if="!isPdf"
                        :key="`dre-consumers-distribution-${metric.id}`"
                        :quick-action-group-id="`dre-consumers-distribution-${metric.id}`"
                        :short-description="quickActionLabel(metric.id)"
                        :long-description="quickActionLabel(metric.id)"
                        tooltip="Algumas divergências podem ser encontradas nos valores das ações rápidas devido ao descadastramento de consumidores durante o período analisado do Faturamento incremental."
                        :segmentation-group="criteriaGroup(getConsumersDistributionCriteria(metric.id))"
                      />
                      <span class="percentage">{{ metric.percentage | localeString(1) }}%</span>
                    </div>
                  </div>
                </template>

                <div class="tooltip-card">
                  <h2 :style="`color: ${metric.tooltipColor}`">{{ metric.group }}</h2>
                  <span v-html-safe="getTooltipDescription(metric.id)" :style="`color: ${metric.tooltipColor}`"></span>
                  <p class="mt-4">
                    Consumidores: <b>{{ metric.clients | localeString }}</b>
                  </p>
                  <p>
                    % da base: <b>{{ metric.percentage | localeString(1) }}%</b>
                  </p>
                </div>
              </mf-tooltip>
            </div>
          </div>
        </div>
      </div>
    </template>
  </distribution-card>
</template>

<script>
import LoadingState from '@/modules/home/views/components/LoadingState.vue'
import DistributionCard from './DistributionCard.vue'
import ApexChart from 'vue-apexcharts'
import CardEmptyState from '@/components/CardEmptyState.vue'

import { localeString } from '@/config/helpers.js'
import { mapGetters, mapState, mapActions } from 'vuex'
import {
  criteriaGroup,
  activesConsumersDistributionCriteria,
  inactivesConsumersDistributionCriteria,
  atRiskConsumersDistributionCriteria,
  lossesConsumersDistributionCriteria,
  neverBoughtConsumersDistributionCriteria,
  dreIncrementalRevenueDetailsCriteria,
  isLoyalCriteria,
  signupDateCriteria,
} from '@/components/quick-actions/segmentationGroups.js'
import capitalize from 'lodash/capitalize'
import { DateTime } from 'luxon'

export default {
  name: 'ConsumersDistributionCard',
  components: { ApexChart, DistributionCard, LoadingState, CardEmptyState },
  props: {
    variant: {
      type: String,
      default: 'dre',
      validator: (value) => ['', 'dre', 'home'].includes(value),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    analysedPeriodId: {
      type: String,
      default: '',
    },
    analysedPeriod: {
      type: Array,
      default: () => [],
    },
    analysisInitialDate: {
      type: String,
      default: '',
    },
    isPdf: {
      type: Boolean,
      default: false,
    },
    isWeeklyAnalysis: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters([
      'dreClientsDistributionBefore',
      'analyticsConfiguration',
      'homeConsumersDistribution',
      'totalClientesCadastrados',
      'homeClientesCadastrados',
    ]),
    recencyCriteria() {
      return this.$getLoggedUser().recencyCriteria
    },
    ...mapState({
      lastUpdate: (state) => state.lastUpdate,
    }),
    lastUpdateDateTime() {
      if (!this.lastUpdate.last_updated) return null
      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd')
    },
    monthYearFormat() {
      return this.lastUpdateDateTime ? this.lastUpdateDateTime.setLocale('pt-BR').toFormat("MMMM 'de' yyyy") : ''
    },
    isDREVariant() {
      return ['dre'].includes(this.variant)
    },
    newClientsQuickAction() {
      const lastUpdate = this.lastUpdateDateTime ? this.lastUpdateDateTime.toFormat('yyyyMMdd').toString() : ''
      const lastUpdatedMinusThiryDays = this.lastUpdateDateTime.minus({ months: 1 }).toFormat('yyyyMMdd')

      return {
        quickActionGroupId: 'new-clients',
        shortDescription: `Novos cadastros - ${capitalize(this.monthYearFormat)}`,
        longDescription: `Novos cadastros - ${capitalize(this.monthYearFormat)}`,
        segmentationGroup: criteriaGroup([signupDateCriteria(lastUpdatedMinusThiryDays, lastUpdate), isLoyalCriteria(true)]),
      }
    },
    isEmpty() {
      return this.isDREVariant ? !this.dreClientsDistributionBefore.hasOwnProperty('actives') : !this.homeConsumersDistribution.hasOwnProperty('actives')
    },
    metrics() {
      const { actives, atRisk, losses, neverBought, recentInactives } = this.isDREVariant ? this.dreClientsDistributionBefore : this.homeConsumersDistribution
      return [
        {
          id: 'actives',
          group: 'Ativos',
          percentage: actives?.percentage ? actives.percentage * 100 : 0,
          clients: actives?.quantity,
          color: '#0FC94D',
          tooltipColor: '#0A8533',
        },
        {
          id: 'recentInactives',
          group: 'Inativos recentes',
          percentage: recentInactives?.percentage ? recentInactives.percentage * 100 : 0,
          clients: recentInactives?.quantity,
          color: '#F3AF00',
          tooltipColor: '#CC9300',
        },
        {
          id: 'atRisk',
          group: 'Risco de perder',
          percentage: atRisk?.percentage ? atRisk.percentage * 100 : 0,
          clients: atRisk?.quantity,
          color: '#F36600',
          tooltipColor: '#E56000',
        },
        {
          id: 'losses',
          group: 'Perdidos',
          percentage: losses?.percentage ? losses.percentage * 100 : 0,
          clients: losses?.quantity,
          color: '#B3280D',
          tooltipColor: '#B3280D',
        },
        {
          id: 'neverBought',
          group: 'Nunca compraram',
          percentage: neverBought?.percentage ? neverBought.percentage * 100 : 0,
          clients: neverBought?.quantity,
          color: '#C3C8CD',
          tooltipColor: '#6B7580',
        },
      ]
    },
    quantityTotal() {
      if (!this.isDREVariant) return this.totalClientesCadastrados
      let total = 0
      const clientsDistribution = this.isDREVariant ? this.dreClientsDistributionBefore : this.homeConsumersDistribution

      for (const key in clientsDistribution) {
        if (clientsDistribution.hasOwnProperty(key) && clientsDistribution[key].quantity) {
          total += clientsDistribution[key].quantity
        }
      }

      return total
    },
    series() {
      return this.metrics.map((metric) => metric.percentage)
    },
    options() {
      return {
        chart: {
          type: 'donut',
        },
        colors: this.metrics.map((metric) => metric.color),
        labels: this.metrics.map((metric) => metric.group),
        dataLabels: {
          enabled: false,
        },
        legend: {
          enabled: false,
          show: false,
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
          },
        },
        states: {
          active: {
            filter: {
              type: 'none',
            },
          },
        },
        tooltip: {
          y: {
            formatter: function (value) {
              return localeString(value, 1) + '%'
            },
            title: {
              formatter: function (seriesName) {
                return seriesName + ':'
              },
            },
          },
        },
      }
    },
  },
  methods: {
    ...mapActions(['saveSegment', 'setQuickAction']),
    localeString,
    criteriaGroup,
    async handleRedirectToRfv() {
      const description = 'Faturamento incremental - Total'
      const path = '/clientes/classificacao-rfv'
      const quickAction = criteriaGroup(dreIncrementalRevenueDetailsCriteria(this.analyticsConfiguration._id, this.analysedPeriodId, 4))
      const segment = {
        name: description,
        description,
        hidden: true,
        auto: true,
        fixed: true,
        data: {
          groups: [
            {
              criteria: quickAction.criteria,
              operator: 'AND',
            },
          ],
          operator: 'OR',
        },
      }
      const hiddenSegment = await this.saveSegment(segment)
      this.setQuickAction({ segmentation: { segment: hiddenSegment, title: description, description } })

      const newTab = this.$router.resolve({ path, query: { quick_action: true } })
      window.open(newTab.href, '_blank')
    },
    getConsumersDistributionCriteria(metricId) {
      const analysisInitialDate = DateTime.fromFormat(this.analysisInitialDate, 'yyyyMMdd')
        .plus({ days: this.isDREVariant ? 0 : 1 })
        .toFormat('yyyyMMdd')

      const criterias = {
        actives: activesConsumersDistributionCriteria(
          this.isDREVariant,
          this.analyticsConfiguration._id,
          this.analysedPeriodId,
          this.getRecencyCriteriaDates('actives'),
          analysisInitialDate
        ),
        recentInactives: inactivesConsumersDistributionCriteria(
          this.isDREVariant,
          this.analyticsConfiguration._id,
          this.analysedPeriodId,
          this.getRecencyCriteriaDates('recent_inactives'),
          analysisInitialDate
        ),
        atRisk: atRiskConsumersDistributionCriteria(
          this.isDREVariant,
          this.analyticsConfiguration._id,
          this.analysedPeriodId,
          this.getRecencyCriteriaDates('at_risk'),
          analysisInitialDate
        ),
        losses: lossesConsumersDistributionCriteria(
          this.isDREVariant,
          this.analyticsConfiguration._id,
          this.analysedPeriodId,
          this.getRecencyCriteriaDates('losses'),
          analysisInitialDate
        ),
        neverBought: neverBoughtConsumersDistributionCriteria(this.isDREVariant, this.analyticsConfiguration._id, this.analysedPeriodId, analysisInitialDate),
      }

      return criterias[metricId]
    },
    getTooltipDescription(groupId) {
      const descriptions = {
        actives: `Clientes que compraram nos últimos <b>${this.recencyCriteria.actives.bought[1]}</b> dias.`,
        recentInactives: `Clientes que <b>não</b> compraram nos últimos <b>${this.recencyCriteria.recent_inactives.not_bought[1]}</b> dias, mas compraram nos últimos <b>${this.recencyCriteria.recent_inactives.bought[1]}</b>.`,
        atRisk: `Clientes que <b>não</b> compraram nos últimos <b>${this.recencyCriteria.at_risk.not_bought[1]}</b> dias, mas compraram nos últimos <b>${this.recencyCriteria.at_risk.bought[1]}</b>.`,
        losses: `Clientes que <b>não</b> compraram nos últimos <b>${this.recencyCriteria.losses.not_bought[1]}</b> dias, mas já compraram.`,
        neverBought: `Clientes que <b>nunca</b> realizaram uma compra depois do cadastro.`,
      }

      return descriptions[groupId]
    },
    quickActionLabel(id) {
      const labels = {
        actives: 'Clientes ativos antes do ínicio do Faturamento incremental',
        recentInactives: 'Clientes que estavam inativos antes do ínicio do Faturamento incremental',
        atRisk: 'Clientes inativos antes do ínicio do Faturamento incremental',
        losses: 'Clientes perdidos antes do ínicio do Faturamento incremental',
        neverBought: 'Clientes novos antes do ínicio do Faturamento incremental',
      }

      return labels[id]
    },

    getRecencyCriteriaDates(classification) {
      const date =
        this.isDREVariant && !this.isWeeklyAnalysis
          ? DateTime.fromFormat(this.analysisInitialDate, 'yyyyMMdd').minus({ months: 1 }).endOf('month').toFormat('yyyy-MM-dd')
          : DateTime.fromFormat(this.analysisInitialDate, 'yyyyMMdd').toFormat('yyyy-MM-dd')
      if (!this.recencyCriteria) throw new Error()

      if (classification === 'never_bought') {
        return null
      }

      const { bought, not_bought } = this.recencyCriteria[classification]
      const boughtDate = this.formatedRecencyCriteriaForDate(date, bought)
      const notBoughtDate = this.formatedRecencyCriteriaForDate(date, not_bought)

      const formatDate = (date) => ({
        initialDate: DateTime.fromJSDate(date.initialDate, { zone: 'UTC-3' }).toFormat('yyyyMMdd'),
        finalDate: DateTime.fromJSDate(date.finalDate, { zone: 'UTC-3' }).toFormat('yyyyMMdd'),
      })

      return { boughtDate: formatDate(boughtDate), notBoughtDate: formatDate(notBoughtDate) }
    },

    formatedRecencyCriteriaForDate(date, recencyCriteria) {
      const recencyCriteriaDates = recencyCriteria.map((subtractDays) => {
        if (isNaN(subtractDays)) return 0
        const subtractDate = this.subtractDaysFromDate(date, subtractDays)
        return subtractDate.getTime()
      })

      const initialDate = Math.min(...recencyCriteriaDates) === 0 ? null : new Date(Math.min(...recencyCriteriaDates))

      return {
        initialDate,
        finalDate: new Date(Math.max(...recencyCriteriaDates)),
      }
    },

    subtractDaysFromDate(date, daysMinus) {
      return DateTime.fromFormat(date, 'yyyy-MM-dd').minus({ days: daysMinus }).toJSDate()
    },
  },
}
</script>

<style lang="scss" scoped>
.consumers-distribution-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  .header-description {
    color: $cinza-0;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    width: 100%;
    margin-top: 15px;
  }
  .chart-container {
    display: grid;
    grid-template-columns: 1fr 2fr;
    margin-top: 20px;
    justify-content: center;
    .under-chart-label {
      line-height: 17px;
      color: $cinza-0;
      font-size: 14px;

      b {
        color: $cinza-d10;
      }

      .highlight {
        color: $verde-0;
      }
    }
    .chart-legend-container {
      width: 100%;
      max-width: 100%;
      justify-content: space-evenly !important;
      .chart-legend-header {
        color: $marca-0-l10;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        margin-bottom: 12px;
      }
      .chart-legend-header,
      .legend-label {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }
      .chart-legend-labels {
        display: flex;
        flex-direction: column;
        gap: 12px;
        color: $cinza-d10;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        .legend-label {
          .chart-group {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            gap: 6px;
            .group-color-indicator {
              border-radius: 4px;
              width: 12px;
              height: 12px;
            }
          }
        }

        .percentage {
          color: $cinza-l40;
          font-weight: 700;
          font-size: 14px;
          padding-left: 8px;
          border-left: 1px solid $cinza-l40;
          display: inline-block;
          margin-left: 8px;
        }
      }
    }
  }
}
::v-deep .apexcharts-canvas {
  position: relative;
  display: flex;
  justify-content: flex-start;
  margin-right: -30px;
  svg {
    width: 100% !important;
    margin-left: -38px;
  }
}

.tooltip-card {
  h2 {
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    margin-bottom: 2px;
  }

  span {
    font-size: 12px;
    line-height: 14.4px;
  }

  p {
    color: $cinza-0;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 6px;

    b {
      color: $marca-0-l10;
    }
  }
}
</style>
